/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { InfoBlocks, type InfoItem } from 'ui/molecules/InfoBlocks/InfoBlocks';
import { OurAwards } from 'ui/molecules/OurAwards/OurAwards';
import { TestimonialsCarousel } from 'ui/organisms/TestimonialsCarousel/TestimonialsCarousel';
import { TrustedBy } from 'ui/organisms/TrustedBy/TrustedBy';
import { UseCases } from 'ui/organisms/UseCases/UseCases';
import { COVERED_COUNTRIES_Q2_2026 } from 'ui/organisms/GlobalCoverageSection/GlobalCoverageSection.data';
import { GlobalCoverageSection } from 'ui/organisms/GlobalCoverageSection/GlobalCoverageSection';

import { CompanyDataByIndustry } from 'features/company-data/organisms/CompanyDataByIndustry';
import { CompanyDataCallToAction } from 'features/company-data/organisms/CompanyDataCallToAction/CompanyDataCallToAction';

import styles from './CompanyDataPage.module.scss';

// import { Highlight } from 'ui/atoms/Highlight/Highlight';
// import { Image } from 'ui/atoms/Image';
// import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';

type Translate = (key: string) => string;

const getDataCategories = (t: Translate): InfoItem[] => [
  {
    icon: 'payg-business-identity',
    title: t('Registry data'),
    items: [
      t('Company legal name'),
      t('Registration number, VAT, EIN, LEI'),
      t('Incorporation date'),
      t('Status & legal form'),
      t('Registered address'),
      t('Industry classification'),
    ],
  },
  {
    icon: 'firmographic-data-icon',
    title: t('Financials'),
    items: [
      t('Profit & loss'),
      t('Balance sheet'),
      t('Income statement'),
      t('Cash flow'),
      t('KPI & ratios'),
      t('20 years historic data'),
    ],
  },
  {
    icon: 'payg-ownership-chains',
    title: t('Group structure'),
    items: [
      t('Global corporate linkage'),
      t('Ultimate parent'),
      t('Subsidiaries'),
      t('Ownership path / chain of control'),
      t('Historical parent chains'),
    ],
  },
  {
    icon: 'payg-ownership-structure',
    title: t('Ownership'),
    items: [
      t('Shareholder name'),
      t('Number of shares'),
      t('Share type & value'),
      t('UBO (ultimate beneficial owner) status'),
      t('Date of birth'),
      t('Registered address'),
    ],
  },
  {
    icon: 'payg-people',
    title: t('People data'),
    items: [
      t('Full name'),
      t('Job title'),
      t('Appointment date'),
      t('Nationality'),
      t('Direct email & phone number'),
      t('Social media profiles'),
    ],
  },
  {
    icon: 'card1',
    title: t('Firmographic data'),
    items: [
      t('Website'),
      t('Social media profiles'),
      t('Business description'),
      t('Website traffic'),
      t('Industry tags'),
      t('Estimates (revenue & headcount)'),
    ],
  },
];

const CompanyDataPage = () => {
  const { secondaryNavigation, data } = useContext(ContentContext);
  const { t } = useInt();
  const dataCategories = getDataCategories(t);

  return (
    <div className={styles.wrapper}>
      {data.slug !== 'company-data-by-industry' && (
        <GlobalCoverageSection
          title={t('Company data by country')}
          description={t('Choose country or region of your interest')}
          countries={COVERED_COUNTRIES_Q2_2026}
        />
      )}

      {data.slug === 'company-data-by-industry' && <CompanyDataByIndustry />}

      {/*<HeroSection
        icon={<Image src="img/company-data-globe.png" width={72} height={72} alt="" priority />}
        title={
          <>
            {'Company data by '}
            <Highlight color="blue">country</Highlight>
          </>
        }
        description="Instant access to trusted, continuously updated company data—everything you need to analyze markets, assess risks, and make smarter decisions."
        actions={
          <>
            {secondaryNavigation?.book_demo?.slug && (
              <Button type="dark-outline" size="xl" route={secondaryNavigation.book_demo.slug}>
                Get a quote
              </Button>
            )}

            {secondaryNavigation?.free_trial?.slug && (
              <Button type="dark" size="xl" route={secondaryNavigation.free_trial.slug}>
                Request a Demo
              </Button>
            )}
          </>
        }
      />*/}

      <UseCases
        title={t('Access our data')}
        description={t(
          'Get reliable company data through multiple access points, including API, platform, browser extensions, and MCP.',
        )}
        layout="grid-2-2"
        iconPosition="right"
        background="linear-gradient(90deg, #041854 0%, #10419d 100%)"
        hasHover={false}
        dataCases={[
          {
            title: 'API',
            description: t(
              'Access our data programmatically with a powerful, scalable API that delivers real-time, verified company information directly into your systems and workflows.',
            ),
            icon: 'puzzle-alt',
          },
          {
            title: t('Platform'),
            description: t(
              'Explore and analyze global company data through an intuitive, feature-rich platform designed for research, lead generation, compliance, and risk assessment.',
            ),
            icon: 'dashboard-monitor',
          },
          {
            title: t('Extensions'),
            description: t(
              'Enhance your existing tools with seamless browser and CRM extensions that bring verified company and contact data right into your daily workflow.',
            ),
            icon: 'web-design',
          },
          {
            title: 'MCP',
            description: t(
              'Connect Global Database directly to Claude, ChatGPT, and Cursor via the Model Context Protocol — query verified company data without leaving your AI workflow.',
            ),
            icon: 'mcp-integration',
          },
        ]}
        actionBlock={
          <>
            <TitleBlock
              tag="h3"
              theme="dark"
              title={t('Integrate company intelligence into your product, workflow, or internal systems with ease.')}
              description={t('See our platform in action and explore how it can benefit your business.')}
            />

            {secondaryNavigation?.book_demo?.slug && (
              <Button type="white" size="xl" route={secondaryNavigation.book_demo.slug}>
                {t('Request a demo')}
              </Button>
            )}
          </>
        }
      />

      <InfoBlocks
        theme="light"
        layout="column"
        title={t('Deep, accurate, and actionable company data')}
        description={t(
          'Everything you need to evaluate partners, onboard clients, and power your compliance and sales workflows.',
        )}
        items={dataCategories}
      />

      <CompanyDataCallToAction />

      <TrustedBy />

      <TestimonialsCarousel />

      <OurAwards
        title={t('Stay Ahead with Global Database Excellence and Recognition')}
        description={t("We Adhere to the Industry's Highest Standards of Excellence")}
        variant="split"
      />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default CompanyDataPage;
